/**
 * Shared vocabulary for the detail-view manager. Import these instead of typing
 * the raw strings so a registry key and an `open()` call can never silently
 * drift apart.
 */

/** The mode ("name") a detail view is opened in. */
export const DETAIL_VIEW_MODE = Object.freeze({
  QUERY: 'query', // read-only detail panel
  COMMAND: 'command', // create / edit form
});

/** The domain entity ("type") a detail view belongs to. */
export const DETAIL_VIEW_ENTITY = Object.freeze({
  CLIENT: 'client',
  FRAMEWORK_AGREEMENT: 'frameworkAgreement',
  INQUIRY: 'inquiry',
});

/**
 * Client category passed as the `type` prop to the Client detail views — a
 * client vs. one of its sub-groups.
 *
 * NOTE: many ClientDetails widget internals still use the raw 'client' /
 * 'group' literals; adopt these as those files are touched.
 */
export const CLIENT_TYPE = Object.freeze({
  CLIENT: 'client',
  GROUP: 'group',
});

/** Compose a `<entity>.<mode>` registry key. */
const composeViewKey = (entity, mode) => `${entity}.${mode}`;

/**
 * Canonical registry keys — the single place `<entity>.<mode>` strings are
 * formed. Used both as keys in the registry and as the argument to
 * `useDetailViews().open(...)`, so the two stay in lockstep.
 */
export const DETAIL_VIEW = Object.freeze({
  CLIENT_QUERY: composeViewKey(
    DETAIL_VIEW_ENTITY.CLIENT,
    DETAIL_VIEW_MODE.QUERY
  ),
  CLIENT_COMMAND: composeViewKey(
    DETAIL_VIEW_ENTITY.CLIENT,
    DETAIL_VIEW_MODE.COMMAND
  ),
  FRAMEWORK_AGREEMENT_QUERY: composeViewKey(
    DETAIL_VIEW_ENTITY.FRAMEWORK_AGREEMENT,
    DETAIL_VIEW_MODE.QUERY
  ),
  FRAMEWORK_AGREEMENT_COMMAND: composeViewKey(
    DETAIL_VIEW_ENTITY.FRAMEWORK_AGREEMENT,
    DETAIL_VIEW_MODE.COMMAND
  ),
  INQUIRY_QUERY: composeViewKey(
    DETAIL_VIEW_ENTITY.INQUIRY,
    DETAIL_VIEW_MODE.QUERY
  ),
  INQUIRY_COMMAND: composeViewKey(
    DETAIL_VIEW_ENTITY.INQUIRY,
    DETAIL_VIEW_MODE.COMMAND
  ),
});

/** Sentinel id for "creating a new record" (add flows). */
export const DETAIL_VIEW_NEW_ID = 'new';

/**
 * Build the stable identity used for stacking (replace vs push): opening a view
 * whose key matches the current top REPLACES it, a different key PUSHES. A
 * null/empty id yields a `<entity>:new` key that never collides with a real
 * record.
 *
 * @param {string} entity - a {@link DETAIL_VIEW_ENTITY} value
 * @param {string|number|null|undefined} id
 * @returns {string} e.g. `client:42` or `client:new`
 */
export const buildDetailEntityKey = (entity, id) =>
  `${entity}:${id == null || id === '' ? DETAIL_VIEW_NEW_ID : id}`;
